import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AgendaComponent.scss";

const FALLBACK_THEME = {
  primary: "#C9A14A",
  secondary: "#F7F1E8",
  accent: "#E6C77A",
  text: "#2B2B2B",
  surface: "#FFFFFF",
  border: "#C9A14A",
  muted: "rgba(43, 43, 43, 0.62)",
};

const normalizeColor = (value) => {
  if (typeof value !== "string") return null;

  const v = value.trim();
  if (!v) return null;

  if (
    v.startsWith("#") ||
    v.startsWith("rgb") ||
    v.startsWith("rgba") ||
    v.startsWith("hsl") ||
    v.startsWith("hsla") ||
    v.startsWith("var(")
  ) {
    return v;
  }

  if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v}`;
  if (/^[0-9A-Fa-f]{3}$/.test(v)) return `#${v}`;

  return v;
};

const pickColor = (...values) => {
  for (const value of values) {
    const normalized = normalizeColor(value);
    if (normalized) return normalized;
  }
  return null;
};

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string") return `rgba(43, 43, 43, ${alpha})`;

  let value = hex.replace("#", "").trim();

  if (value.length === 3) {
    value = value
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (!/^[0-9A-Fa-f]{6}$/.test(value)) {
    return `rgba(43, 43, 43, ${alpha})`;
  }

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const resolveTheme = (themeData = {}) => {
  const nested = themeData?.theme || {};

  const primary =
    pickColor(
      themeData?.primary,
      themeData?.primaryColor,
      nested?.primary,
      nested?.primaryColor
    ) || FALLBACK_THEME.primary;

  const secondary =
    pickColor(
      themeData?.secondary,
      themeData?.secondaryColor,
      nested?.secondary,
      nested?.secondaryColor
    ) || FALLBACK_THEME.secondary;

  const accent =
    pickColor(
      themeData?.accent,
      themeData?.accentColor,
      nested?.accent,
      nested?.accentColor
    ) || FALLBACK_THEME.accent;

  const text =
    pickColor(
      themeData?.text,
      themeData?.textColor,
      nested?.text,
      nested?.textColor
    ) || FALLBACK_THEME.text;

  const surface =
    pickColor(
      themeData?.surface,
      themeData?.surfaceColor,
      nested?.surface,
      nested?.surfaceColor
    ) || FALLBACK_THEME.surface;

  const border =
    pickColor(
      themeData?.border,
      themeData?.borderColor,
      nested?.border,
      nested?.borderColor
    ) || primary;

  return {
    primary,
    secondary,
    accent,
    text,
    surface,
    border,
    muted: hexToRgba(text, 0.62),
    primarySoft08: hexToRgba(primary, 0.08),
    primarySoft16: hexToRgba(primary, 0.16),
    primarySoft55: hexToRgba(primary, 0.55),
    shadowSoft: "rgba(0, 0, 0, 0.08)",
    shadowLight: "rgba(0, 0, 0, 0.05)",
  };
};

const formatTime = (timeValue) => {
  if (!timeValue) return "";

  try {
    if (String(timeValue).includes("AM") || String(timeValue).includes("PM")) {
      return timeValue;
    }

    const [hours, minutes] = String(timeValue).split(":");
    const h = Number(hours);
    const m = Number(minutes);

    if (Number.isNaN(h) || Number.isNaN(m)) return timeValue;

    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    const minuteText = String(m).padStart(2, "0");

    return `${hour12}.${minuteText} ${suffix}`;
  } catch {
    return timeValue;
  }
};

const AgendaComponent = ({
  title = "The Wedding Lineup",
  items,
  weddingData,
}) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const ceremony_type = weddingData?.ceremony_type || "wedding";
  const isHomeComing = ceremony_type === "home coming";

  const resolvedTitle = isHomeComing ? "The Homecoming Lineup" : title;
  const eyebrowText = isHomeComing ? "Our Homecoming" : "Our Celebration";
  const ariaLabel = isHomeComing ? "Homecoming lineup" : "Wedding lineup";

  const theme = useMemo(() => resolveTheme(weddingData || {}), [weddingData]);

  const cssVars = useMemo(
    () => ({
      "--ag-text": theme.text,
      "--ag-muted": theme.muted,
      "--ag-primary": theme.primary,
      "--ag-accent": theme.accent,
      "--ag-secondary": theme.secondary,
      "--ag-surface": theme.surface,
      "--ag-border": theme.border,
      "--ag-line-soft": theme.primarySoft55,
      "--ag-ring-soft": theme.primarySoft08,
      "--ag-divider-soft": theme.primarySoft16,
      "--ag-shadow-soft": theme.shadowSoft,
      "--ag-shadow-light": theme.shadowLight,
    }),
    [theme]
  );

  const defaultItems = useMemo(
    () =>
      isHomeComing
        ? [
            { time: "6.00 PM", label: "Guests arrival" },
            { time: "6.30 PM", label: "Homecoming celebration begins" },
            { time: "7.30 PM", label: "Dinner" },
            { time: "9.00 PM", label: "Celebration continues" },
          ]
        : [
            { time: "9.00 AM", label: "Ceremony begins" },
            { time: "9.15 AM", label: "Poruwa ceremony" },
            { time: "12.00 PM", label: "Lunch" },
            { time: "3.30 PM", label: "Going away" },
          ],
    [isHomeComing]
  );

  const agendaItems = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => ({
        time: formatTime(item?.time),
        label: item?.label || item?.event || "",
      }));
    }

    if (Array.isArray(weddingData?.agenda) && weddingData.agenda.length > 0) {
      return weddingData.agenda
        .filter((item) => item?.event || item?.label || item?.time)
        .map((item) => ({
          time: formatTime(item?.time),
          label: item?.label || item?.event || "",
        }));
    }

    if (!weddingData) return defaultItems;

    if (isHomeComing) {
      return defaultItems;
    }

    return [
      {
        time: formatTime(weddingData?.ceremony_time) || "9.00 AM",
        label: "Ceremony begins",
      },
      {
        time: formatTime(weddingData?.poruwa_time) || "9.15 AM",
        label: "Poruwa ceremony",
      },
      {
        time: "12.00 PM",
        label: "Lunch",
      },
      {
        time: "3.30 PM",
        label: "Going away",
      },
    ];
  }, [items, weddingData, defaultItems, isHomeComing]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`agshow ${visible ? "is-visible" : ""}`}
      aria-label={ariaLabel}
      style={cssVars}
    >
      <div className="agshow__head">
        <p className="agshow__eyebrow">{eyebrowText}</p>
        <h2 className="agshow__title">{resolvedTitle}</h2>
      </div>

      <div className="agshow__list">
        {agendaItems.map((item, i) => (
          <article
            className={`agshow__row ${i % 2 !== 0 ? "agshow__row--reverse" : ""}`}
            key={`${item.time}-${item.label}-${i}`}
          >
            <div className="agshow__timeWrap">
              <span className="agshow__time">{item.time}</span>
            </div>

            <div className="agshow__center">
              <span className="agshow__line" />
              <span className="agshow__dot" />
              <span className="agshow__line" />
            </div>

            <div className="agshow__content">
              <h3 className="agshow__label">{item.label}</h3>
              <p className="agshow__meta">{String(i + 1).padStart(2, "0")}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default AgendaComponent;